import React, { useMemo } from 'react';
import FormLabel from './FormLabel';
import CustomSelect from '../../ui/CustomSelect';
import { formatCurrencyInput, parseCurrency } from './utils/formatCurrency';
import { CREDIT_QUALITY_OPTIONS } from './utils/constants';

interface FinancialTabProps {
  formData: any;
  handleChange: (field: string, value: any) => void;
  errors?: Record<string, string>;
}

export default function FinancialTab({ formData, handleChange, errors = {} }: FinancialTabProps) {
  // Format monthly_income untuk display
  const formattedIncome = useMemo(() => {
    return formatCurrencyInput(formData.monthly_income);
  }, [formData.monthly_income]);

  // Format last_arrears untuk display
  const formattedArrears = useMemo(() => {
    return formatCurrencyInput(formData.last_arrears);
  }, [formData.last_arrears]);

  // Handle currency input change
  const handleIncomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = parseCurrency(e.target.value);
    handleChange('monthly_income', rawValue);
  };

  const handleArrearsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = parseCurrency(e.target.value);
    handleChange('last_arrears', rawValue);
  };
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Data Keuangan</h3>
        <p className="text-gray-500 text-sm">Informasi pekerjaan dan penghasilan</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <FormLabel>Pekerjaan</FormLabel>
          <input
            type="text"
            value={formData.occupation}
            onChange={e => handleChange('occupation', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="Jenis pekerjaan"
          />
        </div>

        <div>
          <FormLabel>Nama Perusahaan</FormLabel>
          <input
            type="text"
            value={formData.company_name}
            onChange={e => handleChange('company_name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="Nama tempat bekerja"
          />
        </div>

        <div>
          <FormLabel>Penghasilan Bulanan (Rp)</FormLabel>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
              Rp
            </span>
            <input
              type="text"
              value={formattedIncome}
              onChange={handleIncomeChange}
              className={`w-full pl-12 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors ${
                errors.monthly_income ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="0"
            />
          </div>
          {errors.monthly_income && (
            <p className="text-sm text-red-600 mt-1">{errors.monthly_income}</p>
          )}
        </div>

        <div>
          <FormLabel>Sumber Penghasilan</FormLabel>
          <input
            type="text"
            value={formData.source_of_income}
            onChange={e => handleChange('source_of_income', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="Sumber penghasilan utama"
          />
        </div>

        <div>
          <FormLabel>Tanggal Mulai Hubungan Bank</FormLabel>
          <input
            type="date"
            value={formData.relationship_start_date}
            onChange={e => handleChange('relationship_start_date', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          />
        </div>
        <div>
          <FormLabel>Hubungan dengan Bank</FormLabel>
          <input
            type="text"
            value={formData.bank_relationship}
            onChange={e => handleChange('bank_relationship', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="Contoh: Nasabah Lama / Baru"
          />
        </div>
        <div>
          <FormLabel>Fasilitas Pembiayaan Terakhir</FormLabel>
          <input
            type="text"
            value={formData.last_facility}
            onChange={e => handleChange('last_facility', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="Fasilitas kredit terakhir"
          />
        </div>
        <div>
          <FormLabel>Kualitas Kredit Terakhir</FormLabel>
          <CustomSelect
            value={formData.last_credit_quality}
            onChange={v => handleChange('last_credit_quality', v)}
            placeholder="Pilih"
            width="w-full"
            options={CREDIT_QUALITY_OPTIONS}
          />
        </div>
        <div>
          <FormLabel>Tunggakan Terakhir (Rp)</FormLabel>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
              Rp
            </span>
            <input
              type="text"
              value={formattedArrears}
              onChange={handleArrearsChange}
              className={`w-full pl-12 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors ${
                errors.last_arrears ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="0"
            />
          </div>
          {errors.last_arrears && (
            <p className="text-sm text-red-600 mt-1">{errors.last_arrears}</p>
          )}
        </div>
      </div>
    </div>
  );
}
